import { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { useCourtroomRooms } from "../../../../hooks/useCourtroomRooms";
import { recordingSocket } from "../../../../components/mediasoup/recordingSocket";
import { CourtScene } from "../main-dahsboard/CourtroomCardThumb";
import { StatusPill } from "../helpers/StatusPill";
import type { Flag } from "../helpers/types";

interface Props {
  selectedRoomId?: string;
  onSelectRoom: (room: any) => void;
  flags: Flag[];
}

function roomFlagBadge(roomId: string, flags: Flag[]) {
  const roomFlags = flags.filter((f) => f.rm?.includes(roomId));
  const hasActive = roomFlags.some(
    (f) => !f.rv && f.flagStatus !== "dismissed",
  );
  const hasAny = roomFlags.length > 0 && !hasActive;
  if (hasActive) {
    return (
      <div className="absolute top-1 right-1 text-[8px] bg-red-500 text-white px-1.5 py-0.5 rounded-[3px]">
        ⚑
      </div>
    );
  }
  if (hasAny) {
    return (
      <div className="absolute top-1 right-1 text-[8px] bg-amber-500 text-white px-1.5 py-0.5 rounded-[3px]">
        ⚑
      </div>
    );
  }
  return null;
}

export default function LiveCourtroomsSidebar({
  selectedRoomId,
  onSelectRoom,
  flags,
}: Readonly<Props>) {
  const { rooms, isLoading } = useCourtroomRooms();
  const [roomSnapUrls, setRoomSnapUrls] = useState<Record<string, string>>({});
  const STREAMING_BACKEND_URL = import.meta.env.VITE_APP_STREAM_SERVER;

  useEffect(() => {
    const handleSnapshot = (payload: {
      courtRoomNo: string;
      frameUrl: string;
    }) => {
      const url = `${STREAMING_BACKEND_URL}${payload.frameUrl}`;
      setRoomSnapUrls((prev) => ({ ...prev, [payload.courtRoomNo]: url }));
    };

    recordingSocket.on("snapshot_frame", handleSnapshot);
    return () => {
      recordingSocket.off("snapshot_frame", handleSnapshot);
    };
  }, [STREAMING_BACKEND_URL]);

  return (
    <aside className="w-80 shrink-0 pt-1 border-l bg-white/75 border-white/10 flex flex-col ">
      <div className="px-4 py-1 border-b border-white/10">
        <div className="flex items-center justify-between">
          <h3 className="text-gray-500 text-xs font-[450] uppercase">
            Other Rooms
          </h3>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2.5 flex flex-col gap-2.5">
        {isLoading && (
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-28 rounded-lg bg-white/5 animate-pulse"
              />
            ))}
          </div>
        )}

        {!isLoading && rooms.length === 0 && (
          <div className="p-6 text-center text-white/40 text-sm">
            No courtrooms found
          </div>
        )}

        {!isLoading &&
          rooms.map((room, idx) => {
            const isActive = selectedRoomId === room.id;
            return (
              <button
                key={room.id}
                type="button"
                onClick={() => !isActive && onSelectRoom(room)}
                className={`w-full text-left rounded-lg overflow-hidden border  transition-all ${
                  isActive
                    ? "border-2 border-blue-500 cursor-default"
                    : "border-white/10 hover:border-white/30 cursor-pointer"
                }`}
              >
                <div className="relative w-full aspect-video overflow-hidden ">
                  <CourtScene
                    room={room}
                    idx={idx}
                    snapUrl={roomSnapUrls[room.id] ?? null}
                  />

                  <div
                    className="absolute top-1 left-1 text-[9px] text-white  px-1.5 py-0.5
                   rounded-[3px] flex items-center gap-1"
                  >
                    {room.status === "live" && (
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse shrink-0" />
                    )}
                    {room.id}
                  </div>

                  {roomFlagBadge(room.id, flags)}

                  {(room.vw ?? 0) > 0 && (
                    <div className="absolute bottom-1 left-1.5 text-gray-500 text-[9px] flex items-center gap-1">
                      <Eye size={9} /> {room.vw}
                    </div>
                  )}
                </div>

                <div className="px-2.5 py-2">
                  <div className="text-[11px] font-semibold text-gray-500 mb-px">
                    {room.id} · {room.blk}
                  </div>
                  <div className="text-[10px] text-gray-500 truncate ">
                    {room.cs}
                  </div>
                  <StatusPill st={room.status} />
                  {isActive && (
                    <div className="text-[10px] text-blue-400 font-medium mt-1 flex items-center gap-1">
                      <Eye size={11} /> Viewing now
                    </div>
                  )}
                </div>
              </button>
            );
          })}
      </div>
    </aside>
  );
}
